/**
 * Words for the parcel size, the "deliver or leave for collection" choice, the price shown before a
 * request is sent, and the recipient's collection code. The Arabic and Darija strings are drafts
 * that need a native review, like the other recent modules.
 */
export const pricingMessages = {
  fr: {
    stepParcel: 'Votre colis',
    modeTitle: 'Que voulez-vous faire ?',
    modeDeliver: 'Le faire livrer',
    modeDeliverHint: 'Un livreur l’apporte près du destinataire.',
    modeHold: 'Le laisser pour retrait',
    modeHoldHint: 'Il reste au point de dépôt. Le destinataire vient le chercher.',
    sizeTitle: 'Taille du colis',
    size: { small: 'Petit', medium: 'Moyen', large: 'Grand' },
    sizeHint: {
      small: 'Documents, téléphone, petits objets',
      medium: 'Une boîte à chaussures, un petit sac',
      large: 'Un grand carton, une valise'
    },
    sizeIncluded: 'Inclus',
    sizeExtra: '+ {amount}',
    priceTitle: 'Prix',
    priceTotal: 'Total à payer',
    priceNeedBoth: 'Choisissez le point de dépôt et le point de livraison pour voir le prix.',
    priceNeedDropoff: 'Choisissez le point de dépôt pour voir le prix.',
    priceLoading: 'Calcul du prix…',
    priceError: 'Impossible de calculer le prix pour le moment.',
    priceRetry: 'Réessayer',
    priceNote: 'Vous payez quand le gardien accepte votre demande.',
    lineBase: 'Frais de base',
    lineDistance: 'Distance ({km} km)',
    lineHold: 'Dépôt et retrait au même point',
    lineSize: 'Taille : {size}',
    lineMinimum: 'Tarif minimum',
    holdWhere: 'Le destinataire vient chercher le colis à ce point.',
    holdCodeNote: 'Il reçoit un code par message. Il doit le montrer à l’agent pour prendre le colis.',
    summaryMode: 'Mode',
    summarySize: 'Taille',
    summaryPrice: 'Prix',
    holdDestination: 'Retrait au point de dépôt',
    recipientCodeTitle: 'Code du destinataire',
    recipientCodeHint: 'Demandez au destinataire le code à 4 chiffres reçu par message, puis saisissez-le.',
    recipientCodeMissing: 'Saisissez le code à 4 chiffres du destinataire.',
    stepReady: 'Prêt à retirer',
    stepCollected: 'Retiré',
    holdBadge: 'Retrait sur place',
    waitingForRecipient: 'En attente du destinataire',
    handToRecipient: 'Remettre au destinataire',
    uncollected: {
      title: 'Colis non retirés ({count})',
      hint: 'Colis restés au point relais plus de {days} jours sans que le destinataire vienne.',
      recipient: 'Destinataire : {name}',
      waiting: '{days} j d’attente'
    },
    resend: {
      action: 'Renvoyer le code',
      hint: 'Le destinataire ({name}) peut ne pas avoir reçu son code de retrait. Renvoyez-le-lui par message.',
      sent: 'Code renvoyé au destinataire.',
      failed: 'Impossible de renvoyer le code.'
    },
    admin: {
      title: 'Tarifs des envois',
      intro: 'Le prix d’un envoi est calculé avec ces tarifs et montré au client avant l’envoi. Les montants sont en dirhams.',
      base: 'Frais de base (livraison)',
      perKm: 'Prix par kilomètre',
      min: 'Prix minimum (livraison)',
      hold: 'Prix du retrait sur place',
      sizeSmall: 'Supplément petit colis',
      sizeMedium: 'Supplément colis moyen',
      sizeLarge: 'Supplément grand colis',
      detour: 'Détour routier (%)',
      detourHint: 'La distance en ligne droite est multipliée par ce pourcentage pour estimer la route. 130 % est un bon point de départ.',
      holdDays: 'Jours d’attente au point',
      holdDaysHint: 'Au-delà, un colis non retiré est signalé à l’administration.',
      example: 'Exemple : 8 km, colis moyen',
      exampleLine: '{total} pour la livraison · {hold} pour le retrait sur place',
      legacyFeeHint: 'Utilisé seulement quand aucun prix calculé ne s’applique (anciennes demandes sans point de dépôt).',
      marginTitle: 'Marge au prix le plus bas',
      margin_delivery: 'Livraison (prix minimum, petit colis)',
      margin_hold: 'Retrait sur place (petit colis)',
      marginLine: '{price} encaissés · {cost} versés · marge {margin}',
      marginLossTitle: 'Un envoi au prix le plus bas fait perdre de l’argent',
      marginLossHint: 'Les commissions des agents et du livreur sont fixes. Augmentez le prix minimum ou le prix du retrait, ou baissez les commissions.',
      saved: 'Tarifs enregistrés.'
    }
  },
  en: {
    stepParcel: 'Your parcel',
    modeTitle: 'What do you want to do?',
    modeDeliver: 'Have it delivered',
    modeDeliverHint: 'A courier brings it near the recipient.',
    modeHold: 'Leave it for collection',
    modeHoldHint: 'It stays at the drop-off point. The recipient comes to get it.',
    sizeTitle: 'Parcel size',
    size: { small: 'Small', medium: 'Medium', large: 'Large' },
    sizeHint: {
      small: 'Documents, a phone, small items',
      medium: 'A shoe box, a small bag',
      large: 'A big box, a suitcase'
    },
    sizeIncluded: 'Included',
    sizeExtra: '+ {amount}',
    priceTitle: 'Price',
    priceTotal: 'Total to pay',
    priceNeedBoth: 'Choose the drop-off point and the delivery point to see the price.',
    priceNeedDropoff: 'Choose the drop-off point to see the price.',
    priceLoading: 'Working out the price…',
    priceError: 'The price cannot be worked out right now.',
    priceRetry: 'Try again',
    priceNote: 'You pay when the guardian accepts your request.',
    lineBase: 'Base fee',
    lineDistance: 'Distance ({km} km)',
    lineHold: 'Drop-off and collection at the same point',
    lineSize: 'Size: {size}',
    lineMinimum: 'Minimum fee',
    holdWhere: 'The recipient collects the parcel at this point.',
    holdCodeNote: 'They get a code by message. They must show it to the agent to take the parcel.',
    summaryMode: 'Mode',
    summarySize: 'Size',
    summaryPrice: 'Price',
    holdDestination: 'Collected at the drop-off point',
    recipientCodeTitle: 'Recipient’s code',
    recipientCodeHint: 'Ask the recipient for the 4-digit code they received by message, then enter it.',
    recipientCodeMissing: 'Enter the recipient’s 4-digit code.',
    stepReady: 'Ready to collect',
    stepCollected: 'Collected',
    holdBadge: 'Collect on site',
    waitingForRecipient: 'Waiting for the recipient',
    handToRecipient: 'Hand to the recipient',
    uncollected: {
      title: 'Parcels not collected ({count})',
      hint: 'Parcels left at a relay for more than {days} days without the recipient coming.',
      recipient: 'Recipient: {name}',
      waiting: '{days} d waiting'
    },
    resend: {
      action: 'Send the code again',
      hint: 'The recipient ({name}) may not have received their collection code. Send it to them again by message.',
      sent: 'Code sent again to the recipient.',
      failed: 'The code could not be sent again.'
    },
    admin: {
      title: 'Delivery prices',
      intro: 'The price of a parcel is worked out from these rates and shown to the customer before they send it. Amounts are in dirhams.',
      base: 'Base fee (delivery)',
      perKm: 'Price per kilometre',
      min: 'Minimum price (delivery)',
      hold: 'Price to leave it for collection',
      sizeSmall: 'Small parcel surcharge',
      sizeMedium: 'Medium parcel surcharge',
      sizeLarge: 'Large parcel surcharge',
      detour: 'Road detour (%)',
      detourHint: 'The straight-line distance is multiplied by this percentage to estimate the road. 130% is a good start.',
      holdDays: 'Days waiting at the point',
      holdDaysHint: 'After this, a parcel nobody collected is flagged to the admin.',
      example: 'Example: 8 km, medium parcel',
      exampleLine: '{total} for delivery · {hold} to leave for collection',
      legacyFeeHint: 'Used only when no calculated price applies (old requests without a drop-off point).',
      marginTitle: 'Margin at the lowest price',
      margin_delivery: 'Delivery (minimum price, small parcel)',
      margin_hold: 'Leave for collection (small parcel)',
      marginLine: '{price} collected · {cost} paid out · margin {margin}',
      marginLossTitle: 'A parcel at the lowest price loses money',
      marginLossHint: 'Agent and courier pay is fixed. Raise the minimum or the collection price, or lower the commissions.',
      saved: 'Prices saved.'
    }
  },
  ar: {
    stepParcel: 'طردك',
    modeTitle: 'ماذا تريد أن تفعل؟',
    modeDeliver: 'توصيله',
    modeDeliverHint: 'يوصله موزّع إلى مكان قريب من المستلم.',
    modeHold: 'تركه للاستلام',
    modeHoldHint: 'يبقى في نقطة الإيداع، ويأتي المستلم لأخذه.',
    sizeTitle: 'حجم الطرد',
    size: { small: 'صغير', medium: 'متوسط', large: 'كبير' },
    sizeHint: {
      small: 'وثائق، هاتف، أغراض صغيرة',
      medium: 'علبة حذاء، حقيبة صغيرة',
      large: 'صندوق كبير، حقيبة سفر'
    },
    sizeIncluded: 'مشمول',
    sizeExtra: '+ {amount}',
    priceTitle: 'السعر',
    priceTotal: 'المبلغ الإجمالي',
    priceNeedBoth: 'اختر نقطة الإيداع ونقطة التسليم لرؤية السعر.',
    priceNeedDropoff: 'اختر نقطة الإيداع لرؤية السعر.',
    priceLoading: 'جارٍ حساب السعر…',
    priceError: 'تعذّر حساب السعر الآن.',
    priceRetry: 'إعادة المحاولة',
    priceNote: 'تدفع عندما يقبل الوكيل طلبك.',
    lineBase: 'الرسوم الأساسية',
    lineDistance: 'المسافة ({km} كم)',
    lineHold: 'الإيداع والاستلام في النقطة نفسها',
    lineSize: 'الحجم: {size}',
    lineMinimum: 'الحد الأدنى للسعر',
    holdWhere: 'يستلم المستلم الطرد من هذه النقطة.',
    holdCodeNote: 'يصله رمز برسالة، ويجب أن يُريه للوكيل ليأخذ الطرد.',
    summaryMode: 'الطريقة',
    summarySize: 'الحجم',
    summaryPrice: 'السعر',
    holdDestination: 'الاستلام من نقطة الإيداع',
    recipientCodeTitle: 'رمز المستلم',
    recipientCodeHint: 'اطلب من المستلم الرمز المكوّن من 4 أرقام الذي وصله برسالة، ثم أدخله.',
    recipientCodeMissing: 'أدخل رمز المستلم المكوّن من 4 أرقام.',
    stepReady: 'جاهز للاستلام',
    stepCollected: 'تم الاستلام',
    holdBadge: 'استلام في عين المكان',
    waitingForRecipient: 'في انتظار المستلم',
    handToRecipient: 'تسليم للمستلم',
    uncollected: {
      title: 'طرود لم تُستلم ({count})',
      hint: 'طرود بقيت في نقطة التسليم أكثر من {days} أيام دون أن يأتي المستلم.',
      recipient: 'المستلم: {name}',
      waiting: 'في الانتظار {days} يوم'
    },
    resend: {
      action: 'إعادة إرسال الرمز',
      hint: 'قد لا يكون المستلم ({name}) قد تلقى رمز الاستلام. أعد إرساله إليه برسالة.',
      sent: 'تم إرسال الرمز مرة أخرى إلى المستلم.',
      failed: 'تعذّر إعادة إرسال الرمز.'
    },
    admin: {
      title: 'أسعار الشحنات',
      intro: 'يُحسب سعر الشحنة من هذه الأسعار ويُعرض على الزبون قبل الإرسال. المبالغ بالدرهم.',
      base: 'الرسوم الأساسية (التوصيل)',
      perKm: 'السعر لكل كيلومتر',
      min: 'الحد الأدنى للسعر (التوصيل)',
      hold: 'سعر الترك للاستلام',
      sizeSmall: 'إضافة الطرد الصغير',
      sizeMedium: 'إضافة الطرد المتوسط',
      sizeLarge: 'إضافة الطرد الكبير',
      detour: 'الالتفاف الطرقي (%)',
      detourHint: 'تُضرب المسافة المستقيمة في هذه النسبة لتقدير الطريق. 130% بداية جيدة.',
      holdDays: 'أيام الانتظار في النقطة',
      holdDaysHint: 'بعدها يُبلَّغ المشرف عن الطرد الذي لم يستلمه أحد.',
      example: 'مثال: 8 كم، طرد متوسط',
      exampleLine: '{total} للتوصيل · {hold} للترك والاستلام',
      legacyFeeHint: 'يُستعمل فقط حين لا ينطبق أي سعر محسوب (طلبات قديمة بلا نقطة إيداع).',
      marginTitle: 'الهامش عند أدنى سعر',
      margin_delivery: 'التوصيل (الحد الأدنى للسعر، طرد صغير)',
      margin_hold: 'الترك للاستلام (طرد صغير)',
      marginLine: 'المحصَّل {price} · المدفوع {cost} · الهامش {margin}',
      marginLossTitle: 'شحنة بأدنى سعر تُحقق خسارة',
      marginLossHint: 'عمولات الوكلاء والموزع ثابتة. ارفع الحد الأدنى للسعر أو سعر الترك للاستلام، أو خفّض العمولات.',
      saved: 'تم حفظ الأسعار.'
    }
  },
  ma: {
    stepParcel: 'الكولي ديالك',
    modeTitle: 'شنو بغيتي دير؟',
    modeDeliver: 'يتوصل',
    modeDeliverHint: 'واحد الليفرور كيوصلو حدا المستلم.',
    modeHold: 'نخليه باش يتاخد',
    modeHoldHint: 'كيبقى فنقطة الإيداع، والمستلم كيجي ياخدو.',
    sizeTitle: 'حجم الكولي',
    size: { small: 'صغير', medium: 'وسط', large: 'كبير' },
    sizeHint: {
      small: 'ورايق، تيليفون، حوايج صغار',
      medium: 'علبة د الصباط، ساك صغير',
      large: 'كارتون كبير، ڤاليز'
    },
    sizeIncluded: 'داخل',
    sizeExtra: '+ {amount}',
    priceTitle: 'الثمن',
    priceTotal: 'المجموع اللي غادي تخلص',
    priceNeedBoth: 'ختار نقطة الإيداع ونقطة التسليم باش تشوف الثمن.',
    priceNeedDropoff: 'ختار نقطة الإيداع باش تشوف الثمن.',
    priceLoading: 'كنحسبو الثمن…',
    priceError: 'ما قدرناش نحسبو الثمن دابا.',
    priceRetry: 'عاود',
    priceNote: 'كتخلص ملي الكارديان يقبل الطلب ديالك.',
    lineBase: 'الثمن د الأساس',
    lineDistance: 'المسافة ({km} كلم)',
    lineHold: 'الإيداع والاستلام فنفس النقطة',
    lineSize: 'الحجم: {size}',
    lineMinimum: 'أقل ثمن',
    holdWhere: 'المستلم كيجي ياخد الكولي من هاد النقطة.',
    holdCodeNote: 'كتوصلو رسالة فيها كود، خاصو يوريه للكارديان باش ياخد الكولي.',
    summaryMode: 'الطريقة',
    summarySize: 'الحجم',
    summaryPrice: 'الثمن',
    holdDestination: 'كيتاخد من نقطة الإيداع',
    recipientCodeTitle: 'كود المستلم',
    recipientCodeHint: 'سول المستلم على الكود د 4 أرقام اللي جاتو فالرسالة، ودخلو.',
    recipientCodeMissing: 'دخل كود المستلم د 4 أرقام.',
    stepReady: 'واجد باش يتاخد',
    stepCollected: 'تاخد',
    holdBadge: 'كيتاخد فعين المكان',
    waitingForRecipient: 'كنتسناو المستلم',
    handToRecipient: 'عطيه للمستلم',
    uncollected: {
      title: 'كوليات ما تاخدوش ({count})',
      hint: 'كوليات بقاو فنقطة التسليم أكثر من {days} أيام والمستلم ما جاش.',
      recipient: 'المستلم: {name}',
      waiting: 'كيتسنى {days} يوم'
    },
    resend: {
      action: 'عاود صيفط الكود',
      hint: 'يمكن المستلم ({name}) ما وصلوش كود الاستلام. عاود صيفطو ليه فرسالة.',
      sent: 'الكود تصيفط عاود للمستلم.',
      failed: 'ما قدرناش نعاودو نصيفطو الكود.'
    },
    admin: {
      title: 'أثمنة الشحن',
      intro: 'ثمن الكولي كيتحسب بهاد الأثمنة وكيتعرض على الزبون قبل ما يصيفط. المبالغ بالدرهم.',
      base: 'الثمن د الأساس (التوصيل)',
      perKm: 'الثمن د كل كيلومتر',
      min: 'أقل ثمن (التوصيل)',
      hold: 'ثمن التخلية باش يتاخد',
      sizeSmall: 'زيادة الكولي الصغير',
      sizeMedium: 'زيادة الكولي الوسط',
      sizeLarge: 'زيادة الكولي الكبير',
      detour: 'دورة الطريق (%)',
      detourHint: 'المسافة المستقيمة كتتضرب فهاد النسبة باش نقدرو الطريق. 130% بداية مزيانة.',
      holdDays: 'عدد الأيام د الانتظار فالنقطة',
      holdDaysHint: 'من بعد، الكولي اللي ما تاخدش كيتبلغ للأدمين.',
      example: 'مثال: 8 كلم، كولي وسط',
      exampleLine: '{total} للتوصيل · {hold} للتخلية والاستلام',
      legacyFeeHint: 'كيتستعمل غير فين ما كاينش ثمن محسوب (طلبات قديمة بلا نقطة إيداع).',
      marginTitle: 'الربح فأقل ثمن',
      margin_delivery: 'التوصيل (أقل ثمن، كولي صغير)',
      margin_hold: 'التخلية باش يتاخد (كولي صغير)',
      marginLine: 'تحصل {price} · تخلص {cost} · الربح {margin}',
      marginLossTitle: 'شحنة بأقل ثمن كتخسر',
      marginLossHint: 'عمولات الكارديان والليفرور ثابتة. طلع أقل ثمن أو ثمن التخلية، ولا نقص العمولات.',
      saved: 'تسجلو الأثمنة.'
    }
  }
}
